import { emphasize } from '@mui/material';
import Color from 'color';

export default ({ config, palette, ...theme }: any) => {
  return {
    root: {
      flexGrow: 1,

      '& > div > div': {
        backgroundColor: palette.background.default,
      },
    },
    input: {
      display: 'flex',
      padding: 0,
      backgroundColor: palette.background.default,
    },
    valueContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      flex: 1,
      alignItems: 'center',
    },
    chip: {
      margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
      backgroundColor: 'initial',
      borderRadius: '0',
      '& > span': {
        padding: 0,
      },
    },
    chipFocused: {
      backgroundColor: emphasize(
        palette.mode === 'light' ? palette.grey[300] : palette.grey[700],
        0.08
      ),
    },
    noOptionsMessage: {
      padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
    },
    singleValue: {
      fontSize: 16,
    },
    placeholder: {
      position: 'absolute',
      left: 2,
      fontSize: 16,
    },
    /* The menu now sits in the Popover that owns its placement and stacking, so it no longer
       positions or raises itself. Absolute with left/right 0 and z-index 100 is what kept it
       inside the nearest scrolling ancestor -- a dialog's content area clipped it at the footer
       divider, and no z-index could have helped because clipping is not stacking. */
    paper: {
      width: '100%',
      border: `1px solid ${palette.primary.oldBorder}`,

      '& > div > div': {
        marginTop: 0,
      },
    },
    divider: {
      height: theme.spacing.unit * 2,
    },

    buttonDown: {
      position: 'absolute',
      top: '50%',
      right: '5px',
      transform: 'translateY(-50%)',
    },

    menuList: {
      width: 'calc(100% + 2px)',
      maxHeight: '220px',
      marginLeft: '-1px',
      position: 'relative',
      overflowY: 'scroll',
      backgroundColor: palette.background.paper,
      border: `1px solid ${palette.primary.oldBorder}`,
      borderTop: 'none',
    },
    select: {
      padding: '11.2px',
      position: 'relative',
      border: `1px solid ${palette.common.almostWhite}`,
      textAlign: 'left',
      textTransform: 'none',
      lineHeight: 1,
      fontWeight: 400,
      borderRadius: 5,
      overflow: 'auto',
      scrollBehavior: 'smooth',

      '& > span': {
        display: 'block',
        width: '100%',
        textAlign: 'left',
      },

      '&:hover': {
        background: Color(palette.common.darkBlue).darken(0.1).string(),
      },
    },

    menuItem: {
      paddingLeft: 0,
    },

    isDisabled: {
      opacity: 0.5,
    },

    label: {
      marginTop: '0.5rem',
      marginLeft: '0.833rem',
      paddingBottom: '0.5rem',
      display: 'flex',
      fontSize: config.textSizes.normal,
      color: palette.text.text8,
    },

    legendItem: {
      display: 'flex',
      flexDirection: 'row',
      margin: '0.5rem 0 0 0.833rem',
      paddingBottom: '0.5rem',
      cursor: 'pointer',
      borderBottom: `1px solid ${palette.primary.modalBorder}`,
    },
    newLegendItem: {
      display: 'flex',
      flexDirection: 'row',
      cursor: 'pointer',
      alignItems: 'center',
      paddingBottom: '0.5rem',
      paddingTop: '0.5rem',
      borderBottom: `1px solid ${palette.primary.modalBorder}`,
    },
    legendIcon: {
      width: '0.833rem',
      height: '0.833rem',
      backgroundColor: palette.primary.main,
      margin: '3px 10px 0 0',
    },
    emptyLegendIcon: {
      width: '0.833rem',
      height: '0.833rem',
      border: `1px solid ${palette.primary.main}`,
      margin: '3px 10px 0 0',
    },
    legendText: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.33,
      color: palette.primary.contrastText,
      margin: '0px !important',
    },
    errorText: {
      visibility: 'visible',
      color: palette.text.text9,
    },

    noErrorText: {
      visibility: 'hidden',
    },
  };
};

/**
 * The menu, as it has to be inside the popover that now hosts it.
 *
 * <p>react-select's own base style is `position: absolute`. In flow that let the menu hang below
 * the control and be clipped by whatever scrolled above it; in the popover it means the menu
 * contributes no height at all, so the paper sized itself to the search box -- forty pixels -- and
 * scrolled every option out of sight. Static puts it back in flow and the paper grows to fit.
 *
 * <p>The elevation and the gap go with it: the popover draws its own shadow, and the base margin
 * would open a strip of paper above the first option.
 *
 * <p>One definition rather than the same object written into both style sets, which differ only in
 * whether the control is shown.
 */
const menuInPopover = (palette: any) => (base: any) => ({
  ...base,
  position: 'static',
  marginTop: 0,
  marginBottom: 0,
  boxShadow: 'none',

  '& .MuiMenuItem-root': {
    backgroundColor: palette.background.paper,
  },
});

export const MultiSelectStyles = ({ palette }: any) => {
  return {
    container: (base: any) => ({
      ...base,

      '& > div': {
        border: 'none !important',
        boxShadow: 'none !important',
      },
    }),
    control: (base: any) => ({
      ...base,
      position: 'relative',
      backgroundColor: palette.background.default,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      color: palette.text.primary,

      '& *': {
        color: palette.text.primary,
      },

      '& svg': {
        color: palette.text.primary,
        position: 'absolute',
        top: '50%',
        right: 7,
        transform: 'translateY(-50%)',
        fontSize: 18,
      },
    }),
    menu: menuInPopover(palette),
  };
};

export const MultiSelectStyles1 = ({ palette }: any) => {
  return {
    container: (base: any) => ({
      ...base,

      '& > div': {
        border: 'none !important',
        boxShadow: 'none !important',
      },
    }),
    control: () => ({
      display: 'none',
    }),
    menu: menuInPopover(palette),
  };
};
